import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState
} from "react";
import {
  TableBody,
  TableBulkItemRow,
  TableBulkItemHeader,
  toggleItemWithSet,
  itemCheckedWithSet,
  toggleAll,
  HEADER_ID,
  TableHeaderItem,
  TableSearchEdit,
  sortTableData,
  getColumns
} from "collections/Table";
import { handleNullFields, nameIsUnassigned } from "utilities/stringUtils";
import { useCountDevicesQuery, Room } from "generated/graphql";
import { LoadingIndicator, PolyIcon } from "elements";
import { useTranslation } from "utilities/i18n";
import { Link, generatePath, infiniteNestLink } from "polyrouter";
import { SortState } from "store/filters";
import { TABLES, TABLE_COLUMN_STATE } from "store/userMeta";
import { useSelector } from "react-redux";
import { State } from "store";
import { ProtectedLink } from "TsRoleGuards";
import { generateTableHeaders } from "utilities/tableUtils";

type RoomData = Pick<Room, "id" | "name" | "capacity" | "floor">;

type RoomDetails = RoomData & {
  deviceCount?: number;
  percentOnline?: number;
  updatesAvailable?: string;
};

type RoomRowProps = {
  room: RoomDetails;
  onCheck: (id: string) => void;
  checked: boolean;
  showChecks: boolean;
  updateSortedRooms: (room: RoomDetails) => void;
  shownColumns: Array<string>;
};

export const RoomRow: React.FC<RoomRowProps> = ({
  room,
  onCheck,
  checked,
  showChecks = true,
  updateSortedRooms,
  shownColumns
}) => {
  const { COLUMNS, HIDDEN } = TABLES.SITE_ROOMS;
  const { id, name, capacity, floor } = room;

  const { data, fetching } = useCountDevicesQuery({
    variables: {
      totalCountFilter: {
        AND: [
          {
            eq: id,
            field: "roomUUID"
          }
        ]
      },
      connectedCountFilter: {
        AND: [
          {
            eq: id,
            field: "roomUUID"
          },
          {
            eq: "true",
            field: "connected"
          }
        ]
      }
    }
  });

  const deviceCount = data?.count;
  const percentOnline = Math.round(
    ((data?.connectedCount || 0) / (deviceCount || 1)) * 100
  );

  useEffect(() => {
    if (data) {
      updateSortedRooms({
        ...room,
        deviceCount,
        percentOnline
      });
    }
  }, [data, room, deviceCount, percentOnline, updateSortedRooms]);

  const isUnassigned = nameIsUnassigned(name);

  const getTableContent = (column: string) => {
    let content: ReactNode = null;
    switch (column) {
      case COLUMNS.NAME:
        content = (
          <Link to={generatePath("/manage/rooms/details/:id", { id })}>
            {handleNullFields(name)}
          </Link>
        );
        break;
      case COLUMNS.DEVICES:
        content = fetching ? (
          <LoadingIndicator className="inline text-left" title=" " />
        ) : (
          deviceCount
        );
        break;
      case COLUMNS.PERCENT_ONLINE:
        content = fetching ? (
          <LoadingIndicator className="inline text-left" title=" " />
        ) : (
          percentOnline
        );
        break;
      case COLUMNS.CAPACITY:
        content = capacity ?? "-";
        break;
      case HIDDEN.FLOOR:
        content = handleNullFields(floor, true);
        break;
    }

    return <td key={column}>{content}</td>;
  };
  return (
    <TableBulkItemRow
      showChecks={showChecks}
      checked={checked}
      onCheck={() => onCheck(id)}
      replaceCheckWith={
        isUnassigned ? <PolyIcon icon="warning_triangle" /> : undefined
      }
    >
      {shownColumns.map(getTableContent)}
    </TableBulkItemRow>
  );
};

export const RoomsTable: React.FC<{
  rooms: Array<RoomData>;
  fetching: boolean;
  selectedItems: Array<string>;
  setSelectedItems: Dispatch<SetStateAction<string[]>>;
  showChecks: boolean;
}> = ({
  rooms,
  fetching,
  selectedItems,
  setSelectedItems,
  showChecks = true
}) => {
  const [sort, setSort] = useState({
    field: "name",
    direction: "ASC"
  } as SortState);
  const [searchTerm, setSearch] = useState<undefined | string>(undefined);
  const { NAME: TABLE_NAME, COLUMNS, HIDDEN } = TABLES.SITE_ROOMS;
  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });

  const { t } = useTranslation();

  const toggleItem = toggleItemWithSet(
    rooms.length,
    selectedItems,
    setSelectedItems
  );
  const itemChecked = itemCheckedWithSet(selectedItems);

  let sortedRooms: Array<RoomDetails> = Array.from(rooms);
  // Filter rooms
  if (!!searchTerm) {
    sortedRooms = sortedRooms.filter(room =>
      room.name.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())
    );
  }
  // Sort rooms
  sortTableData(sortedRooms, sort);

  // This is a callback prop to be passed down to the lower components.
  // Once a lower component is finished fetching the data,
  // it will update the corresponding object in state here, so the table can be sorted.
  const updateSortedRooms = (room: RoomDetails) => {
    const { id, deviceCount, percentOnline, updatesAvailable } = room;
    let roomToUpdate = sortedRooms.find(room => room.id === id);
    if (roomToUpdate) {
      roomToUpdate.deviceCount = deviceCount;
      roomToUpdate.percentOnline = percentOnline;
      roomToUpdate.updatesAvailable = updatesAvailable;
    }
  };

  const columnTranslations = {
    [COLUMNS.NAME]: t("ROOM"),
    [COLUMNS.DEVICES]: t("DEVICES"),
    [COLUMNS.PERCENT_ONLINE]: t("PERCENT_ONLINE"),
    [COLUMNS.CAPACITY]: t("CAPACITY"),
    [HIDDEN.FLOOR]: t("FLOOR")
  };

  const getTableHeaders = (column: string, title: string) => (
    <TableHeaderItem
      fieldName={column}
      title={title}
      sort={{
        currentSort: sort,
        onSort: setSort
      }}
      key={column}
    />
  );

  const shown = getColumns(shownColumns, columnTranslations);
  const hidden = getColumns(hiddenColumns, columnTranslations);

  return (
    <>
      <div className="pb-1 table-actions">
        <ProtectedLink
          to={infiniteNestLink("/add")}
          className="btn btn-primary"
          requirement="UPDATE_SITES"
        >
          {t("ADD_NEW")}
        </ProtectedLink>
        {selectedItems.length > 0 ? (
          <ProtectedLink
            to={infiniteNestLink("/remove")}
            className="btn"
            data-testid="remove-room"
            requirement="UPDATE_SITES"
          >
            {t("REMOVE_ROOM", {
              count: selectedItems.filter(item => item !== HEADER_ID).length
            })}
          </ProtectedLink>
        ) : (
          <ProtectedLink
            to={infiniteNestLink("/remove")}
            className="btn disabled"
            data-testid="remove-room"
            requirement="UPDATE_SITES"
          >
            {t("REMOVE_ROOM")}
          </ProtectedLink>
        )}
        <TableSearchEdit
          search={{
            onSearch: setSearch,
            searchPlaceholder: t("SEARCH_ROOM")
          }}
          edit={{
            tableName: TABLE_NAME,
            shownColumns: shown,
            hiddenColumns: hidden
          }}
        />
      </div>
      <div className="table-container">
        <table className="table custom-table">
          <TableBulkItemHeader
            checked={itemChecked(HEADER_ID)}
            showChecks={showChecks && !!shown.length}
            onCheck={() =>
              toggleAll(
                rooms
                  .filter(room => !nameIsUnassigned(room.name))
                  .map(room => room.id),
                selectedItems,
                setSelectedItems
              )
            }
          >
            {generateTableHeaders(shown, getTableHeaders)}
          </TableBulkItemHeader>
          <TableBody
            loading={fetching}
            cols={shown.length}
            accommodateScrollbar
          >
            {sortedRooms.map(room => {
              return (
                <RoomRow
                  room={room}
                  key={room.id}
                  onCheck={() => toggleItem(room.id)}
                  checked={itemChecked(room.id)}
                  showChecks={showChecks && !!shown.length}
                  updateSortedRooms={updateSortedRooms}
                  shownColumns={shown.map(column => column.key)}
                />
              );
            })}
          </TableBody>
        </table>
      </div>
    </>
  );
};
